import { useState } from 'react';
import { DateTimeInput, Form, Title, useNotify } from 'react-admin';
import { fetchTherapistsStatisticsBlob } from 'features/therapists-statistics';
import styled from 'styled-components';

const now = new Date();
const defaultStartDate = new Date(
  now.getFullYear(),
  now.getMonth() - 1,
).toISOString();
const defaultEndDate = new Date().toISOString();

type FormValues = {
  startDate?: Date | string;
  endDate?: Date | string;
};

const toIsoString = (value?: Date | string) => {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.toISOString();
};

const formatDateForFilename = (value: string) => {
  const date = new Date(value);

  return [
    String(date.getUTCDate()).padStart(2, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    date.getUTCFullYear(),
  ].join('.');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const tempAnchor = document.createElement('a');

  tempAnchor.setAttribute('href', url);
  tempAnchor.setAttribute('download', filename);
  tempAnchor.click();
  tempAnchor.remove();
  window.URL.revokeObjectURL(url);
};

const TherapistsStatistics = () => {
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  const handleSubmit = async ({ startDate, endDate }: FormValues) => {
    const startDateIso = toIsoString(startDate);
    const endDateIso = toIsoString(endDate);

    if (!startDateIso || !endDateIso) {
      notify('Укажите начало и конец периода', { type: 'error' });
      return;
    }

    if (new Date(startDateIso) > new Date(endDateIso)) {
      notify('Дата начала периода не может быть позже даты окончания', {
        type: 'error',
      });
      return;
    }

    setIsLoading(true);

    const defaultFilename = `statistics_by_therapists_${formatDateForFilename(startDateIso)}-${formatDateForFilename(endDateIso)}.xlsx`;

    try {
      const result = await fetchTherapistsStatisticsBlob(
        { startDate: startDateIso, endDate: endDateIso },
        defaultFilename,
      );

      if (result.error || !result.blob) {
        notify(result.error || 'Не удалось скачать файл', { type: 'error' });
        return;
      }

      downloadBlob(result.blob, result.filename);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Root>
      <Title title="Статистика по терапевтам" />
      <Form
        onSubmit={handleSubmit}
        defaultValues={{ startDate: defaultStartDate, endDate: defaultEndDate }}
      >
        <div>
          <DateTimeInput
            label="Начало периода"
            source="startDate"
            style={{ marginRight: '10px' }}
          />
          <DateTimeInput label="Конец периода" source="endDate" />
        </div>
        <DownloadBtn type="submit" disabled={isLoading}>
          {isLoading ? 'Загрузка...' : 'Скачать'}
        </DownloadBtn>
      </Form>
    </Root>
  );
};

export default TherapistsStatistics;

const Root = styled.div`
  padding: 20px;
`;

const DownloadBtn = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
